import React from 'react';
import { Box, Typography, Button, Chip, Divider, Avatar, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from '../i18n/i18n';
import PsychometricResultsSummary from './PsychometricResultsSummary';

const primaryButtonSx = { textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } };
const labelSx = { fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 };

function CandidateDetailsPanel({ candidate, isAdmin, onClose, onOpenMenu, onEditVacancies, onRequestDocs, onHire, onViewFull }) {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={0}
      sx={{ position: 'relative', border: 2, borderColor: 'primary.main', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 2, mt: 0, zIndex: 10, minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}
    >
      <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8 }} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      {isAdmin && (
        <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 44 }} onClick={onOpenMenu}>
          <MoreVertIcon />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: 'white', fontSize: '1rem', fontWeight: 600 }}>
          {(candidate.fullname || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{candidate.fullname}</Typography>
          <Typography variant="caption" color="text.secondary">{candidate.email}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={labelSx}>{t('position')}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{candidate.position}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={labelSx}>{t('status')}</Typography>
          <Chip
            label={candidate.status}
            size="small"
            color={candidate.status === 'active' ? 'success' : candidate.status === 'pending' ? 'warning' : 'default'}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('candidate_vacancies')}</Typography>
          {isAdmin && (
            <Button size="small" variant="outlined" onClick={onEditVacancies} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              {t('candidate_edit_vacancies')}
            </Button>
          )}
        </Box>
        {candidate.vacancy_ids?.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {candidate.vacancy_ids.map((v) => (
              <Chip key={v._id} label={v.title} size="small" color="primary" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('candidate_no_vacancies')}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <PsychometricResultsSummary results={candidate.psychometric_test_results} />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {candidate.docs_requested === false ? (
          <Button variant="contained" fullWidth onClick={onRequestDocs} sx={primaryButtonSx}>
            {t('request_docs')}
          </Button>
        ) : (
          <Button variant="contained" fullWidth onClick={onHire} sx={primaryButtonSx}>
            {t('hire_candidate')}
          </Button>
        )}
        <Button variant="outlined" fullWidth onClick={onViewFull} sx={{ textTransform: 'none' }}>
          {t('view_full_info')}
        </Button>
      </Box>
    </Paper>
  );
}

export default CandidateDetailsPanel;
